import React, { useState, useEffect } from 'react'
import { useAuth } from '../../AuthProvider'
import { 
  Users, 
  BookOpen, 
  Plus, 
  Trash2, 
  Check, 
  X, 
  Search,
  ChevronDown,
  UserCheck,
  Settings,
  Eye,
  Edit,
  Shield,
  Megaphone
} from 'lucide-react'
import { getApiBase } from '../../utils/api'
import { InfoTooltip } from './InfoTooltip'
import { PermissionsInfoGroup } from './PermissionsInfoGroup'

interface TeamMember {
  id: string
  email: string
  role_type: string
}

interface TeamBook {
  id: string
  title: string
  author_id: string
}

interface TeamCampaign {
  id: string
  title: string
  author_id: string
}

interface BookPermission {
  user_id: string
  book_id: string
}

interface CampaignPermission {
  user_id: string
  campaign_id: string
}

interface BookPermissionsData {
  teamMembers: TeamMember[]
  teamBooks: TeamBook[]
  permissions: BookPermission[]
}

interface CampaignPermissionsData {
  teamMembers: TeamMember[]
  teamCampaigns: TeamCampaign[]
  permissions: CampaignPermission[]
}

interface QRLink {
  id: string
  title: string
  slug: string
  type: 'website' | 'spotify' | 'youtube' | 'pdf' | 'multishop' | 'author' | 'sample'
  book_id?: string
  campaign_id?: string
  created_at: string
  author: {
    id: string
    email: string
    full_name: string | null
  }
  book?: {
    id: string
    title: string
  }
  campaign?: {
    id: string
    title: string
  }
}

interface QRLinksData {
  qrLinks: QRLink[]
}

type PermissionType = 'books' | 'campaigns' | 'admin' | 'qr-links'

const API_BASE = getApiBase()

export const TeamPermissions: React.FC = () => {
  const { session } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<PermissionType>('books')
  const [bookPermissionsData, setBookPermissionsData] = useState<BookPermissionsData | null>(null)
  const [campaignPermissionsData, setCampaignPermissionsData] = useState<CampaignPermissionsData | null>(null)
  const [qrLinksData, setQRLinksData] = useState<QRLinksData | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingUser, setEditingUser] = useState<TeamMember | null>(null)
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [selectedBooks, setSelectedBooks] = useState<Set<string>>(new Set())
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [creating, setCreating] = useState(false)
  const [selectedPermissionType, setSelectedPermissionType] = useState<'read' | 'write' | 'admin'>('write')

  // Load book permissions data
  const loadBookPermissions = async () => {
    if (!session?.access_token) return

    try {
      const response = await fetch(`${API_BASE}/api/team/book-permissions`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to load book permissions')
      }

      const data = await response.json()
      setBookPermissionsData(data)
    } catch (err) {
      console.error('Failed to load book permissions:', err)
      throw err
    }
  }

  // Load campaign permissions data
  const loadCampaignPermissions = async () => {
    if (!session?.access_token) return

    try {
      const response = await fetch(`${API_BASE}/api/team/campaign-permissions`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to load campaign permissions')
      }

      const data = await response.json()
      setCampaignPermissionsData(data)
    } catch (err) {
      console.error('Failed to load campaign permissions:', err)
      throw err
    }
  }

  // Load QR links data
  const loadQRLinks = async () => {
    if (!session?.access_token) return

    try {
      const response = await fetch(`${API_BASE}/api/team/all-qr-links`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to load QR links')
      }

      const data = await response.json()
      setQRLinksData(data)
    } catch (err) {
      console.error('Failed to load QR links:', err)
      throw err
    }
  }

  // Load all permissions
  const loadPermissions = async () => {
    try {
      setLoading(true)
      setError(null)
      await Promise.all([loadBookPermissions(), loadCampaignPermissions(), loadQRLinks()])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions')
    } finally {
      setLoading(false)
    }
  }

  // Grant book permission
  const grantBookPermission = async (userId: string, bookId: string, permissionType: string = 'read') => {
    if (!session?.access_token) return

    try {
      const response = await fetch(`${API_BASE}/api/team/grant-book-permission`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          book_id: bookId,
          permission_type: permissionType
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to grant book permission')
      }

      return true
    } catch (err) {
      console.error('Failed to grant book permission:', err)
      throw err
    }
  }

  // Revoke book permission
  const revokeBookPermission = async (userId: string, bookId: string) => {
    if (!session?.access_token) return

    try {
      const response = await fetch(`${API_BASE}/api/team/revoke-book-permission`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          book_id: bookId
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to revoke book permission')
      }

      return true
    } catch (err) {
      console.error('Failed to revoke book permission:', err)
      throw err
    }
  }

  // Grant campaign permission
  const grantCampaignPermission = async (userId: string, campaignId: string, permissionType: string = 'read') => {
    if (!session?.access_token) return

    try {
      const response = await fetch(`${API_BASE}/api/team/grant-campaign-permission`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          campaign_id: campaignId,
          permission_type: permissionType
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to grant campaign permission')
      }

      return true
    } catch (err) {
      console.error('Failed to grant campaign permission:', err)
      throw err
    }
  }

  // Revoke campaign permission
  const revokeCampaignPermission = async (userId: string, campaignId: string) => {
    if (!session?.access_token) return

    try {
      const response = await fetch(`${API_BASE}/api/team/revoke-campaign-permission`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          campaign_id: campaignId
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to revoke campaign permission')
      }

      return true
    } catch (err) {
      console.error('Failed to revoke campaign permission:', err)
      throw err
    }
  }

  // Handle bulk permission creation
  const handleCreatePermissions = async () => {
    if (!selectedUser) {
      alert('Bitte wählen Sie einen Benutzer aus.')
      return
    }

    // For admin tab, we don't need specific resources
    if (activeTab === 'admin') {
      if (selectedPermissionType !== 'admin') {
        alert('Für allgemeine Admin-Rechte muss "Admin" als Berechtigungstyp gewählt werden.')
        return
      }
    } else {
      // For books and campaigns, we need at least one resource selected
      if (activeTab === 'books' && selectedBooks.size === 0) {
        alert('Bitte wählen Sie mindestens ein Buch aus oder verwenden Sie den Admin-Tab für allgemeine Rechte.')
        return
      }

      if (activeTab === 'campaigns' && selectedCampaigns.size === 0) {
        alert('Bitte wählen Sie mindestens eine Kampagne aus oder verwenden Sie den Admin-Tab für allgemeine Rechte.')
        return
      }
    }

    setCreating(true)
    try {
      if (activeTab === 'books') {
        // Grant permissions for all selected books
        for (const bookId of selectedBooks) {
          await grantBookPermission(selectedUser, bookId, selectedPermissionType)
        }
        await loadBookPermissions()
        alert(`${selectedPermissionType.charAt(0).toUpperCase() + selectedPermissionType.slice(1)}-Berechtigungen für ${selectedBooks.size} Buch/Bücher erfolgreich erteilt!`)
      } else if (activeTab === 'campaigns') {
        // Grant permissions for all selected campaigns
        for (const campaignId of selectedCampaigns) {
          await grantCampaignPermission(selectedUser, campaignId, selectedPermissionType)
        }
        await loadCampaignPermissions()
        alert(`${selectedPermissionType.charAt(0).toUpperCase() + selectedPermissionType.slice(1)}-Berechtigungen für ${selectedCampaigns.size} Kampagne/Kampagnen erfolgreich erteilt!`)
      } else if (activeTab === 'admin') {
        // Grant general admin permissions (this would need backend implementation)
        alert('Admin-Rechte erfolgreich erteilt! (Feature wird noch implementiert)')
      }
      
      // Reset form
      setSelectedUser('')
      setSelectedBooks(new Set())
      setSelectedCampaigns(new Set())
      setSelectedPermissionType('write')
      setShowCreateModal(false)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Erteilen der Berechtigungen')
    } finally {
      setCreating(false)
    }
  }

  // Handle book permission revocation
  const handleRevokeBookPermission = async (userId: string, bookId: string) => {
    if (!confirm('Berechtigung wirklich entziehen?')) return

    try {
      await revokeBookPermission(userId, bookId)
      await loadBookPermissions()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Entziehen der Berechtigung')
    }
  }

  // Handle campaign permission revocation
  const handleRevokeCampaignPermission = async (userId: string, campaignId: string) => {
    if (!confirm('Berechtigung wirklich entziehen?')) return

    try {
      await revokeCampaignPermission(userId, campaignId)
      await loadCampaignPermissions()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Entziehen der Berechtigung')
    }
  }

  // Toggle book selection
  const toggleBookSelection = (bookId: string) => {
    const newSelected = new Set(selectedBooks)
    if (newSelected.has(bookId)) {
      newSelected.delete(bookId)
    } else {
      newSelected.add(bookId)
    }
    setSelectedBooks(newSelected)
  }

  // Toggle campaign selection
  const toggleCampaignSelection = (campaignId: string) => {
    const newSelected = new Set(selectedCampaigns)
    if (newSelected.has(campaignId)) {
      newSelected.delete(campaignId)
    } else {
      newSelected.add(campaignId)
    }
    setSelectedCampaigns(newSelected)
  }

  // Select all books
  const selectAllBooks = () => {
    if (!bookPermissionsData) return
    
    const filteredBooks = bookPermissionsData.teamBooks.filter(book =>
      book.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
    
    if (selectedBooks.size === filteredBooks.length) {
      setSelectedBooks(new Set())
    } else {
      setSelectedBooks(new Set(filteredBooks.map(book => book.id)))
    }
  }

  // Select all campaigns
  const selectAllCampaigns = () => {
    if (!campaignPermissionsData) return
    
    const filteredCampaigns = campaignPermissionsData.teamCampaigns.filter(campaign =>
      campaign.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
    
    if (selectedCampaigns.size === filteredCampaigns.length) {
      setSelectedCampaigns(new Set())
    } else {
      setSelectedCampaigns(new Set(filteredCampaigns.map(campaign => campaign.id)))
    }
  }

  // Get user email by ID
  const getUserEmail = (userId: string) => {
    const data = activeTab === 'books' ? bookPermissionsData : campaignPermissionsData
    return data?.teamMembers.find(member => member.id === userId)?.email || 'Unbekannt'
  }

  // Get book title by ID
  const getBookTitle = (bookId: string) => {
    return bookPermissionsData?.teamBooks.find(book => book.id === bookId)?.title || 'Unbekannt'
  }

  // Get campaign title by ID
  const getCampaignTitle = (campaignId: string) => {
    return campaignPermissionsData?.teamCampaigns.find(campaign => campaign.id === campaignId)?.title || 'Unbekannt'
  }

  // Check if user has permission for book
  const hasBookPermission = (userId: string, bookId: string) => {
    return bookPermissionsData?.permissions.some(p => p.user_id === userId && p.book_id === bookId) || false
  }

  // Check if user has permission for campaign
  const hasCampaignPermission = (userId: string, campaignId: string) => {
    return campaignPermissionsData?.permissions.some(p => p.user_id === userId && p.campaign_id === campaignId) || false
  }

  // Handle edit user permissions
  const handleEditUser = (user: TeamMember) => {
    setEditingUser(user)
    setSelectedUser(user.id)
    
    // Pre-select current permissions
    if (activeTab === 'books' && bookPermissionsData) {
      const userBookIds = bookPermissionsData.permissions
        .filter(p => p.user_id === user.id)
        .map(p => p.book_id)
      setSelectedBooks(new Set(userBookIds))
    } else if (activeTab === 'campaigns' && campaignPermissionsData) {
      const userCampaignIds = campaignPermissionsData.permissions
        .filter(p => p.user_id === user.id)
        .map(p => p.campaign_id)
      setSelectedCampaigns(new Set(userCampaignIds))
    }
    
    setShowEditModal(true)
  }

  // Handle save edited permissions
  const handleSaveEditedPermissions = async () => {
    if (!editingUser) return

    setCreating(true)
    try {
      if (activeTab === 'books' && bookPermissionsData) {
        // Get current permissions
        const currentBookIds = new Set(
          bookPermissionsData.permissions
            .filter(p => p.user_id === editingUser.id)
            .map(p => p.book_id)
        )
        
        // Find books to add and remove
        const booksToAdd = Array.from(selectedBooks).filter(id => !currentBookIds.has(id))
        const booksToRemove = Array.from(currentBookIds).filter(id => !selectedBooks.has(id))
        
        // Add new permissions
        for (const bookId of booksToAdd) {
          await grantBookPermission(editingUser.id, bookId, selectedPermissionType)
        }
        
        // Remove old permissions
        for (const bookId of booksToRemove) {
          await revokeBookPermission(editingUser.id, bookId)
        }
        
        await loadBookPermissions()
        alert('Buch-Berechtigungen erfolgreich aktualisiert!')
        
      } else if (activeTab === 'campaigns' && campaignPermissionsData) {
        // Get current permissions
        const currentCampaignIds = new Set(
          campaignPermissionsData.permissions
            .filter(p => p.user_id === editingUser.id)
            .map(p => p.campaign_id)
        )
        
        // Find campaigns to add and remove
        const campaignsToAdd = Array.from(selectedCampaigns).filter(id => !currentCampaignIds.has(id))
        const campaignsToRemove = Array.from(currentCampaignIds).filter(id => !selectedCampaigns.has(id))
        
        // Add new permissions
        for (const campaignId of campaignsToAdd) {
          await grantCampaignPermission(editingUser.id, campaignId, selectedPermissionType)
        }
        
        // Remove old permissions
        for (const campaignId of campaignsToRemove) {
          await revokeCampaignPermission(editingUser.id, campaignId)
        }
        
        await loadCampaignPermissions()
        alert('Kampagnen-Berechtigungen erfolgreich aktualisiert!')
      }
      
      // Reset form
      setEditingUser(null)
      setSelectedUser('')
      setSelectedBooks(new Set())
      setSelectedCampaigns(new Set())
      setSelectedPermissionType('write')
      setShowEditModal(false)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Fehler beim Aktualisieren der Berechtigungen')
    } finally {
      setCreating(false)
    }
  }

  // Get book permissions grouped by user
  const getBookPermissionsByUser = () => {
    if (!bookPermissionsData) return []

    const userPermissions = new Map<string, string[]>()
    
    bookPermissionsData.permissions.forEach(permission => {
      if (!userPermissions.has(permission.user_id)) {
        userPermissions.set(permission.user_id, [])
      }
      userPermissions.get(permission.user_id)!.push(permission.book_id)
    })

    return Array.from(userPermissions.entries()).map(([userId, bookIds]) => ({
      user: bookPermissionsData.teamMembers.find(member => member.id === userId),
      books: bookIds.map(bookId => bookPermissionsData.teamBooks.find(book => book.id === bookId)).filter(Boolean)
    })).filter(item => item.user)
  }

  // Get campaign permissions grouped by user
  const getCampaignPermissionsByUser = () => {
    if (!campaignPermissionsData) return []

    const userPermissions = new Map<string, string[]>()
    
    campaignPermissionsData.permissions.forEach(permission => {
      if (!userPermissions.has(permission.user_id)) {
        userPermissions.set(permission.user_id, [])
      }
      userPermissions.get(permission.user_id)!.push(permission.campaign_id)
    })

    return Array.from(userPermissions.entries()).map(([userId, campaignIds]) => ({
      user: campaignPermissionsData.teamMembers.find(member => member.id === userId),
      campaigns: campaignIds.map(campaignId => campaignPermissionsData.teamCampaigns.find(campaign => campaign.id === campaignId)).filter(Boolean)
    })).filter(item => item.user)
  }

  // Get current data based on active tab
  const getCurrentData = () => {
    if (activeTab === 'books') return bookPermissionsData
    if (activeTab === 'campaigns') return campaignPermissionsData
    // For QR links, we return a compatible structure
    return null
  }

  // Get QR links grouped by user for QR-Links tab
  const getQRLinksByUser = () => {
    if (!qrLinksData?.qrLinks) return []

    const userQRLinks = new Map<string, QRLink[]>()
    
    qrLinksData.qrLinks.forEach(qrLink => {
      const userId = qrLink.author.id
      if (!userQRLinks.has(userId)) {
        userQRLinks.set(userId, [])
      }
      userQRLinks.get(userId)!.push(qrLink)
    })

    return Array.from(userQRLinks.entries()).map(([userId, qrLinks]) => ({
      user: qrLinks[0].author, // Use author from first QR link
      qrLinks: qrLinks
    }))
  }

  // Filter items based on search
  const getFilteredItems = () => {
    const data = getCurrentData()
    if (!data || activeTab === 'qr-links') return []

    if (activeTab === 'books') {
      return (data as BookPermissionsData).teamBooks.filter(book =>
        book.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    } else {
      return (data as CampaignPermissionsData).teamCampaigns.filter(campaign =>
        campaign.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
  }

  useEffect(() => {
    loadPermissions()
  }, [session])

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-center mt-2 text-gray-600">Berechtigungen werden geladen...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-red-700">
          ❌ {error}
          <button 
            onClick={loadPermissions}
            className="ml-4 text-sm underline hover:no-underline"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    )
  }

  const currentData = getCurrentData()
  const hasData = activeTab === 'qr-links' ? qrLinksData : 
                  activeTab === 'admin' ? true : // Admin tab always has "data"
                  currentData
  
  if (!hasData) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">
          Keine Berechtigungsdaten verfügbar.
        </div>
      </div>
    )
  }

  const userPermissions = activeTab === 'books' ? getBookPermissionsByUser() : 
                        activeTab === 'campaigns' ? getCampaignPermissionsByUser() : 
                        activeTab === 'admin' ? [] : // Admin tab shows empty for now
                        getQRLinksByUser()
  const filteredItems = getFilteredItems()

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            Berechtigungen verwalten
            <PermissionsInfoGroup />
          </h1>
          <p className="text-gray-600 mt-1">
            Verwalten Sie den Zugriff auf Inhalte für Team-Mitglieder
          </p>
        </div>
        {activeTab !== 'qr-links' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {activeTab === 'admin' ? 'Neue Admin-Berechtigung' : 'Neue Berechtigung'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('books')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'books'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Bücher ({bookPermissionsData?.teamBooks.length || 0})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'campaigns'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Megaphone className="w-4 h-4" />
                Kampagnen ({campaignPermissionsData?.teamCampaigns.length || 0})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('admin')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'admin'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Admin-Rechte
              </div>
            </button>
            <button
              onClick={() => setActiveTab('qr-links')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'qr-links'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                QR-Codes ({qrLinksData?.qrLinks.length || 0})
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">
                Team-Mitglieder
              </p>
              <p className="text-2xl font-bold">
                {activeTab === 'qr-links' 
                  ? getQRLinksByUser().length
                  : activeTab === 'admin'
                  ? (bookPermissionsData?.teamMembers.length || 0) // Use book data for admin
                  : currentData?.teamMembers.length || 0
                }
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            {activeTab === 'books' ? (
              <BookOpen className="w-8 h-8 text-green-600" />
            ) : activeTab === 'campaigns' ? (
              <Megaphone className="w-8 h-8 text-green-600" />
            ) : (
              <BookOpen className="w-8 h-8 text-green-600" />
            )}
            <div>
              <p className="text-sm text-gray-600">
                {activeTab === 'books' ? 'Team-Bücher' : 
                 activeTab === 'campaigns' ? 'Team-Kampagnen' : 
                 activeTab === 'admin' ? 'Admin-Benutzer' :
                 'QR-Codes'}
              </p>
              <p className="text-2xl font-bold">
                {activeTab === 'books' 
                  ? bookPermissionsData?.teamBooks.length || 0
                  : activeTab === 'campaigns'
                  ? campaignPermissionsData?.teamCampaigns.length || 0
                  : activeTab === 'admin'
                  ? 0 // TODO: Count actual admin users
                  : qrLinksData?.qrLinks.length || 0
                }
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <UserCheck className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">
                {activeTab === 'qr-links' ? 'QR-Codes' : 
                 activeTab === 'admin' ? 'Admin-Berechtigungen' :
                 'Aktive Berechtigungen'}
              </p>
              <p className="text-2xl font-bold">
                {activeTab === 'qr-links' 
                  ? qrLinksData?.qrLinks.length || 0
                  : activeTab === 'admin'
                  ? 0 // TODO: Count actual admin permissions
                  : currentData?.permissions.length || 0
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Permissions List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">
            {activeTab === 'books' ? 'Aktuelle Buch-Berechtigungen' : 
             activeTab === 'campaigns' ? 'Aktuelle Kampagnen-Berechtigungen' :
             activeTab === 'admin' ? 'Admin-Berechtigungen Übersicht' :
             'Team QR-Codes Übersicht'}
          </h2>
        </div>
        
        {userPermissions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">
              {activeTab === 'qr-links' ? 'Noch keine QR-Codes erstellt' : 
               activeTab === 'admin' ? 'Noch keine Admin-Berechtigungen vergeben' :
               'Noch keine Berechtigungen vergeben'}
            </h3>
            <p className="mb-4">
              {activeTab === 'qr-links' 
                ? 'Team-Mitglieder haben noch keine QR-Codes erstellt.'
                : activeTab === 'admin'
                ? 'Erteilen Sie Team-Mitgliedern allgemeine Admin-Rechte für Vollzugriff auf alle Funktionen.'
                : `Erteilen Sie Team-Mitgliedern Zugriff auf spezifische ${activeTab === 'books' ? 'Bücher' : 'Kampagnen'}.`
              }
            </p>
            {activeTab !== 'qr-links' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                {activeTab === 'admin' ? 'Erste Admin-Berechtigung erstellen' : 'Erste Berechtigung erstellen'}
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {userPermissions.map((userPerm: any) => (
              <div key={userPerm.user!.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Users className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{userPerm.user!.email}</h3>
                        <p className="text-sm text-gray-500 capitalize">{userPerm.user!.role_type}</p>
                      </div>
                    </div>
                    
                    <div className="ml-11">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-gray-600">
                          {activeTab === 'books' ? (
                            <>Zugriff auf {userPerm.books?.length || 0} Buch{(userPerm.books?.length || 0) !== 1 ? 'er' : ''}:</>
                          ) : activeTab === 'campaigns' ? (
                            <>Zugriff auf {userPerm.campaigns?.length || 0} Kampagne{(userPerm.campaigns?.length || 0) !== 1 ? 'n' : ''}:</>
                          ) : (
                            <>Erstellt {userPerm.qrLinks?.length || 0} QR-Code{(userPerm.qrLinks?.length || 0) !== 1 ? 's' : ''}:</>
                          )}
                        </p>
                        {activeTab !== 'qr-links' && (
                          <button
                            onClick={() => handleEditUser(userPerm.user!)}
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                            title="Berechtigungen bearbeiten"
                          >
                            <Edit className="w-3 h-3" />
                            Bearbeiten
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {activeTab === 'books' ? (
                          userPerm.books?.map((book: any) => (
                            <div
                              key={book!.id}
                              className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                            >
                              <BookOpen className="w-3 h-3" />
                              {book!.title}
                              <button
                                onClick={() => handleRevokeBookPermission(userPerm.user!.id, book!.id)}
                                className="text-red-600 hover:text-red-800 ml-1"
                                title="Berechtigung entziehen"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))
                        ) : activeTab === 'campaigns' ? (
                          userPerm.campaigns?.map((campaign: any) => (
                            <div
                              key={campaign!.id}
                              className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                            >
                              <Megaphone className="w-3 h-3" />
                              {campaign!.title}
                              <button
                                onClick={() => handleRevokeCampaignPermission(userPerm.user!.id, campaign!.id)}
                                className="text-red-600 hover:text-red-800 ml-1"
                                title="Berechtigung entziehen"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))
                        ) : (
                          userPerm.qrLinks?.map((qrLink: any) => (
                            <div
                              key={qrLink.id}
                              className="inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm"
                            >
                              <BookOpen className="w-3 h-3" />
                              {qrLink.title}
                              {qrLink.book && (
                                <span className="text-xs bg-green-200 text-green-700 px-1 rounded">
                                  📖 {qrLink.book.title}
                                </span>
                              )}
                              {qrLink.campaign && (
                                <span className="text-xs bg-blue-200 text-blue-700 px-1 rounded">
                                  📢 {qrLink.campaign.title}
                                </span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Permission Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                Neue {activeTab === 'books' ? 'Buch' : activeTab === 'campaigns' ? 'Kampagnen' : 'Admin'}-Berechtigung erstellen
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Step 1: Select User */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Benutzer auswählen *
                </label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Benutzer auswählen...</option>
                  {(activeTab === 'qr-links' 
                    ? [] // QR-Links don't have create modal
                    : currentData?.teamMembers || []
                  ).map(member => (
                    <option key={member.id} value={member.id}>
                      {member.email} ({member.role_type})
                    </option>
                  ))}
                </select>
              </div>

              {/* Step 2: Select Permission Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Berechtigungstyp auswählen *
                </label>
                <select
                  value={selectedPermissionType}
                  onChange={(e) => setSelectedPermissionType(e.target.value as 'read' | 'write' | 'admin')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="read">Lesen - Kann QR-Codes und {activeTab === 'books' ? 'Bücher' : 'Kampagnen'} ansehen</option>
                  <option value="write">Schreiben - Kann QR-Codes erstellen, bearbeiten und löschen</option>
                  <option value="admin">Admin - Vollzugriff auf alle Funktionen</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedPermissionType === 'read' && `Benutzer kann nur QR-Codes und ${activeTab === 'books' ? 'Bücher' : 'Kampagnen'} ansehen`}
                  {selectedPermissionType === 'write' && 'Benutzer kann QR-Codes erstellen, bearbeiten und löschen'}
                  {selectedPermissionType === 'admin' && 'Benutzer hat Vollzugriff und kann Berechtigungen verwalten'}
                </p>
              </div>

              {/* Step 3: Select Items or Admin Info */}
              {activeTab === 'admin' ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    <h3 className="font-medium text-blue-900">Allgemeine Admin-Rechte</h3>
                  </div>
                  <p className="text-sm text-blue-800">
                    Admin-Rechte gewähren Vollzugriff auf alle Team-Funktionen, ohne spezifische Ressourcen auswählen zu müssen.
                    Der Benutzer kann dann alle Bücher, Kampagnen und QR-Codes verwalten.
                  </p>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      3. {activeTab === 'books' ? 'Bücher' : 'Kampagnen'} auswählen * ({activeTab === 'books' ? selectedBooks.size : selectedCampaigns.size} ausgewählt)
                    </label>
                    <button
                      onClick={activeTab === 'books' ? selectAllBooks : selectAllCampaigns}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {(activeTab === 'books' ? selectedBooks.size : selectedCampaigns.size) === filteredItems.length ? 'Alle abwählen' : 'Alle auswählen'}
                    </button>
                  </div>

                  {/* Search Items */}
                  <div className="relative mb-3">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder={`${activeTab === 'books' ? 'Bücher' : 'Kampagnen'} durchsuchen...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Items List */}
                  <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                    {filteredItems.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        {searchTerm ? `Keine ${activeTab === 'books' ? 'Bücher' : 'Kampagnen'} gefunden` : `Keine ${activeTab === 'books' ? 'Bücher' : 'Kampagnen'} verfügbar`}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {filteredItems.map((item: any) => (
                          <label
                            key={item.id}
                            className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={activeTab === 'books' ? selectedBooks.has(item.id) : selectedCampaigns.has(item.id)}
                              onChange={() => activeTab === 'books' ? toggleBookSelection(item.id) : toggleCampaignSelection(item.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            {activeTab === 'books' ? (
                              <BookOpen className="w-4 h-4 text-gray-400" />
                            ) : (
                              <Megaphone className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="flex-1 text-sm">{item.title}</span>
                            {selectedUser && (activeTab === 'books' ? hasBookPermission(selectedUser, item.id) : hasCampaignPermission(selectedUser, item.id)) && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                                Bereits berechtigt
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreatePermissions}
                disabled={creating || !selectedUser || (activeTab === 'books' ? selectedBooks.size === 0 : activeTab === 'campaigns' ? selectedCampaigns.size === 0 : false)}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Erstelle...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Berechtigungen erteilen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Permission Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                Berechtigungen bearbeiten für {editingUser.email}
              </h2>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingUser(null)
                  setSelectedUser('')
                  setSelectedBooks(new Set())
                  setSelectedCampaigns(new Set())
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Permission Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Berechtigungstyp auswählen *
                </label>
                <select
                  value={selectedPermissionType}
                  onChange={(e) => setSelectedPermissionType(e.target.value as 'read' | 'write' | 'admin')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="read">Lesen - Kann QR-Codes und {activeTab === 'books' ? 'Bücher' : 'Kampagnen'} ansehen</option>
                  <option value="write">Schreiben - Kann QR-Codes erstellen, bearbeiten und löschen</option>
                  <option value="admin">Admin - Vollzugriff auf alle Funktionen</option>
                </select>
              </div>

              {/* Select Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {activeTab === 'books' ? 'Bücher' : 'Kampagnen'} auswählen ({activeTab === 'books' ? selectedBooks.size : selectedCampaigns.size} ausgewählt)
                  </label>
                  <button
                    onClick={activeTab === 'books' ? selectAllBooks : selectAllCampaigns}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {(activeTab === 'books' ? selectedBooks.size : selectedCampaigns.size) === filteredItems.length ? 'Alle abwählen' : 'Alle auswählen'}
                  </button>
                </div>

                {/* Search Items */}
                <div className="relative mb-3">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={`${activeTab === 'books' ? 'Bücher' : 'Kampagnen'} durchsuchen...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Items List */}
                <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                  {filteredItems.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {searchTerm ? `Keine ${activeTab === 'books' ? 'Bücher' : 'Kampagnen'} gefunden` : `Keine ${activeTab === 'books' ? 'Bücher' : 'Kampagnen'} verfügbar`}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {filteredItems.map((item: any) => (
                        <label
                          key={item.id}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={activeTab === 'books' ? selectedBooks.has(item.id) : selectedCampaigns.has(item.id)}
                            onChange={() => activeTab === 'books' ? toggleBookSelection(item.id) : toggleCampaignSelection(item.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          {activeTab === 'books' ? (
                            <BookOpen className="w-4 h-4 text-gray-400" />
                          ) : (
                            <Megaphone className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="flex-1 text-sm">{item.title}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingUser(null)
                  setSelectedUser('')
                  setSelectedBooks(new Set())
                  setSelectedCampaigns(new Set())
                }}
                disabled={creating}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveEditedPermissions}
                disabled={creating}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Speichere...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Änderungen speichern
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 